import {
  ArrowRight,
  ChevronDown,
  PlayCircle,
  RotateCcw,
  Search,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { ehInterno } from '@/nucleo/auth/papeis';
import { useAuth } from '@/nucleo/auth/useAuth';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';

import { useMinhasInscricoes } from './credenciamento/AcompanhamentoDados';
import {
  CAMINHO_DO_ACOMPANHAMENTO,
  CAMINHO_DO_AUTOCADASTRO,
  CAMINHO_DO_PORTAL_DO_CREDENCIADO,
} from './credenciamento/AcompanhamentoContrato';
import { useSessaoDoInscrito } from './credenciamento/identidade/api';
import { PROXIMO_PASSO_CREDENCIAMENTO, type OpcaoDoProximoPasso } from './navegacaoPublica';

interface AcaoPrincipal {
  titulo: string;
  apoio: string;
  rotulo: string;
  caminho: string | null;
  icone: LucideIcon;
}

/**
 * Cartão "Próximo passo" da página Para credenciados (`/credenciados`), igual ao
 * do protótipo (`CredenciadosCtaPanel.tsx`): fica logo abaixo do hero,
 * sobreposto à borda da foto, e muda conforme quem está vendo.
 *
 * - carregando: "Verificando seu status", enquanto as sessões respondem;
 * - credenciado (sessão do Keycloak com papel da empresa credenciada): "Você já
 *   é credenciado", que leva ao Portal do Credenciado;
 * - pré-credenciado (sessão do inscrito com veículo `PRE_CREDENCIADO`): "Você
 *   foi pré-credenciado", que leva ao acompanhamento;
 * - inscrito com inscrição: "Sua inscrição está em andamento", com "Continuar
 *   de onde parei" ao lado;
 * - inscrito sem inscrição: "Tudo pronto para começar", com "Acompanhar
 *   inscrição" ao lado;
 * - quem não entrou: "Quero me credenciar no TEG", com as três portas do menu
 *   "Ver outras opções" (`PROXIMO_PASSO_CREDENCIAMENTO`).
 *
 * Duas diferenças do protótipo, as duas forçadas pelo sistema:
 * - o protótipo tem uma sessão só; aqui o credenciado entra pelo Keycloak e o
 *   inscrito pela sessão própria da inscrição (D-10), então o cartão pergunta às
 *   duas. Quem tem sessão do Keycloak sem ser da empresa credenciada (a equipe
 *   interna) vê o cartão de quem não entrou, porque não se inscreve por ela;
 * - o protótipo tem ainda a versão "rascunho salvo neste navegador". Aqui o
 *   rascunho fica no servidor (RN-CP-26), e quem tem rascunho entra pela sessão
 *   do inscrito e cai na versão de inscrito.
 *
 * O QUE O CARTÃO NÃO TRAZ, DE PROPÓSITO. O protótipo põe, logo abaixo dele, o
 * alerta de status do credenciamento com número de edital, os requisitos, os
 * documentos e a tabela de valores de referência. Tudo isso é conteúdo
 * editorial: vem das seções da página, que a Secretaria publica pelo editor da
 * Administração, e número de edital ou valor em reais escrito aqui seria
 * afirmação que ninguém publicou.
 *
 * A SOBREPOSIÇÃO AO HERO é margem negativa no primeiro filho do miolo da
 * página: o miolo tem `py-10`, e `-mt-16`/`-mt-20` sobem o cartão 24/40 px por
 * cima da foto, menos que o respiro de baixo do hero (`py-12`), então o cartão
 * nunca cobre título nem descrição.
 *
 * "Ver outras opções" é o menu suspenso do protótipo (`Popover`). Os itens são
 * links comuns numa lista, e não `role="menu"`: o padrão de menu do ARIA promete
 * navegação por setas, que um menu de links não tem, e o leitor de tela
 * anunciaria um comportamento que não existe. Pelo teclado, Tab percorre os
 * links e Esc fecha.
 */
export function ProximoPassoCredenciamento() {
  const { estado, usuario } = useAuth();
  const credenciado =
    estado === 'autenticado' && Boolean(usuario?.papeis.some((papel) => !ehInterno(papel)));
  const comSessaoDoKeycloak = estado === 'autenticado';

  const sessao = useSessaoDoInscrito({ habilitada: !comSessaoDoKeycloak });
  const temSessaoDoInscrito = !comSessaoDoKeycloak && Boolean(sessao.data);
  const inscricoes = useMinhasInscricoes(temSessaoDoInscrito);

  const carregando =
    estado === 'iniciando' ||
    estado === 'entrando' ||
    (!comSessaoDoKeycloak && sessao.isPending) ||
    (temSessaoDoInscrito && inscricoes.isPending);

  const entidades = inscricoes.data?.entidades ?? [];
  const preCredenciado = entidades.some((entidade) =>
    entidade.veiculos.some((veiculo) => veiculo.status === 'PRE_CREDENCIADO'),
  );

  let principal: AcaoPrincipal;
  let secundarias: OpcaoDoProximoPasso[] = [];
  let secundariasEmMenu = false;

  if (carregando) {
    principal = {
      titulo: 'Verificando seu status',
      apoio: 'Aguarde um instante enquanto identificamos seu cadastro.',
      rotulo: 'Carregando…',
      caminho: null,
      icone: ArrowRight,
    };
  } else if (credenciado) {
    principal = {
      titulo: 'Você já é credenciado',
      apoio: 'Acesse o portal pelo login institucional para gerenciar seu credenciamento.',
      rotulo: 'Acessar portal do credenciado',
      caminho: CAMINHO_DO_PORTAL_DO_CREDENCIADO,
      icone: ShieldCheck,
    };
  } else if (temSessaoDoInscrito && preCredenciado) {
    principal = {
      titulo: 'Você foi pré-credenciado',
      apoio: 'Falta pouco — confirme os próximos passos para finalizar seu credenciamento.',
      rotulo: 'Confirmar pré-credenciamento',
      caminho: CAMINHO_DO_ACOMPANHAMENTO,
      icone: ShieldCheck,
    };
  } else if (temSessaoDoInscrito && entidades.length > 0) {
    principal = {
      titulo: 'Sua inscrição está em andamento',
      apoio: 'Acompanhe o progresso e veja se há pendências para resolver.',
      rotulo: 'Acompanhar minha inscrição',
      caminho: CAMINHO_DO_ACOMPANHAMENTO,
      icone: Search,
    };
    secundarias = [
      { caminho: CAMINHO_DO_AUTOCADASTRO, titulo: 'Continuar de onde parei', icone: RotateCcw },
    ];
  } else if (temSessaoDoInscrito) {
    principal = {
      titulo: 'Tudo pronto para começar',
      apoio: 'Você está autenticado. Comece agora sua inscrição no programa TEG.',
      rotulo: 'Iniciar minha inscrição',
      caminho: CAMINHO_DO_AUTOCADASTRO,
      icone: PlayCircle,
    };
    secundarias = [
      { caminho: CAMINHO_DO_ACOMPANHAMENTO, titulo: 'Acompanhar inscrição', icone: Search },
    ];
  } else {
    const { inicio, outrasOpcoes } = PROXIMO_PASSO_CREDENCIAMENTO;
    principal = {
      titulo: 'Quero me credenciar no TEG',
      apoio: 'Comece sua inscrição em poucos minutos. Você pode salvar e continuar depois.',
      rotulo: inicio.titulo,
      caminho: inicio.caminho,
      icone: inicio.icone,
    };
    secundarias = [...outrasOpcoes];
    secundariasEmMenu = true;
  }

  const IconePrincipal = principal.icone;

  return (
    <section aria-label="Próximos passos do credenciamento" className="relative -mt-16 sm:-mt-20">
      <Card className="rounded-2xl border-primary/10 shadow-lg">
        <CardContent className="space-y-4 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                Próximo passo
              </p>
              <h2 className="mt-1 text-lg font-semibold leading-tight text-foreground sm:text-xl sm:leading-7">
                {principal.titulo}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">{principal.apoio}</p>
            </div>
            {principal.caminho ? (
              <Button asChild size="lg" className="h-12 w-full shrink-0 text-base sm:w-auto">
                <Link to={principal.caminho}>
                  <span>{principal.rotulo}</span>
                  <IconePrincipal className="h-5 w-5" aria-hidden="true" />
                </Link>
              </Button>
            ) : (
              <Button size="lg" className="h-12 w-full shrink-0 text-base sm:w-auto" disabled>
                <span>{principal.rotulo}</span>
                <IconePrincipal className="h-5 w-5" aria-hidden="true" />
              </Button>
            )}
          </div>

          {secundarias.length > 0 && secundariasEmMenu && (
            <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Já começou antes? Acesse outras opções abaixo.
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-2 h-9 text-sm text-foreground/80 hover:text-foreground sm:ml-0"
                  >
                    <span>Ver outras opções</span>
                    <ChevronDown className="h-4 w-4 opacity-70" aria-hidden="true" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[320px] max-w-[calc(100vw-2rem)] p-1">
                  <ul className="flex flex-col">
                    {secundarias.map((opcao) => {
                      const Icone = opcao.icone;
                      return (
                        <li key={opcao.titulo}>
                          <Link
                            to={opcao.caminho}
                            className="flex items-start gap-3 rounded-md p-3 transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <Icone
                              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                              aria-hidden="true"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-foreground">
                                {opcao.titulo}
                              </span>
                              {opcao.descricao ? (
                                <span className="mt-0.5 block text-xs text-muted-foreground">
                                  {opcao.descricao}
                                </span>
                              ) : null}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {secundarias.length > 0 && !secundariasEmMenu && (
            <div className="flex flex-wrap gap-2 border-t border-border pt-2">
              {secundarias.map((opcao) => {
                const Icone = opcao.icone;
                return (
                  <Button
                    key={opcao.titulo}
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9"
                  >
                    <Link to={opcao.caminho}>
                      <Icone className="h-4 w-4" aria-hidden="true" />
                      <span>{opcao.titulo}</span>
                    </Link>
                  </Button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
